import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@workspace/public-utils'

const AuthCallback = () => {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const navigate = useNavigate()
  const { setUser } = useAuth()

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code')
        const state = searchParams.get('state')
        const error = searchParams.get('error')
        const provider = searchParams.get('provider') // We'll pass this in the state

        if (error) {
          setStatus('error')
          setMessage('인증이 취소되었거나 오류가 발생했습니다.')
          return
        }

        if (!code) {
          setStatus('error')
          setMessage('인증 코드가 없습니다.')
          return
        }

        // Exchange code for tokens
        const base = (() => {
          const raw = (import.meta.env as any).VITE_API_BASE_URL as string | undefined
          return raw && /^(https?:)?\/\//i.test(raw) ? raw : ''
        })()
        const response = await fetch(`${base}/api/v1/auth/oauth/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            state,
            provider: provider || 'google', // fallback
          }),
        })

        const data = await response.json()

        if (response.ok) {
          // Set user context
          setUser(data.user)
          
          setStatus('success')
          setMessage('로그인에 성공했습니다. 잠시 후 이동합니다.')
          
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate('/dashboard')
          }, 2000)
        } else {
          setStatus('error')
          setMessage(data.message || '로그인 처리 중 오류가 발생했습니다.')
        }
      } catch (error) {
        setStatus('error')
        setMessage('네트워크 오류가 발생했습니다.')
      }
    }

    handleCallback()
  }, [searchParams, navigate, setUser])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-happiness-primary to-happiness-secondary rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">8</span>
            </div>
          </div>

          {status === 'loading' && (
            <>
              <div className="spinner mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                로그인 처리 중...
              </h2>
              <p className="text-gray-600">
                잠시만 기다려주세요
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                로그인 성공!
              </h2>
              <p className="text-gray-600">
                {message}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                로그인 실패
              </h2>
              <p className="text-red-600 mb-6">
                {message}
              </p>
              <button
                onClick={() => navigate('/auth/login')}
                className="btn-primary"
              >
                로그인 페이지로 돌아가기
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuthCallback